import { ANIMATION_DURATION, BUFFER_DURATION } from '../constants';
import { CONTENT_CLASS, OVERLAY_CLASS } from '../styles';
import type { SlideOutSide } from '../types';
import { getPanelWidth } from './getPanelWidth';
import { getSideTransform } from './getSideTransform';
import { whenAnimationEnds } from './whenAnimationEnds';

interface PlayCloseAnimationOptions {
  overlay: HTMLElement | null;
  content: HTMLElement | null;
  side: SlideOutSide;
  panelWidth: string;
  onClosed: () => void;
}

/**
 * Plays the panel's exit animation, then calls `onClosed`.
 *
 * `onClosed` runs on `animationend`, or after `ANIMATION_DURATION` plus
 * `BUFFER_DURATION` if that event never fires, so a missed animation cannot
 * leave the panel stuck open.
 *
 * @returns Cleanup that cancels the listener and the fallback timer without calling `onClosed`.
 */
export function playCloseAnimation({
  overlay,
  content,
  side,
  panelWidth,
  onClosed,
}: PlayCloseAnimationOptions): () => void {
  overlay?.classList.remove(OVERLAY_CLASS.show);
  content?.classList.remove(...CONTENT_CLASS.show);

  // Firefox drops `animationend` when an animation class is removed and added
  // again in the same frame, which leaves the panel covering the page.
  // Reading `offsetWidth` flushes layout so the browser treats the hide
  // classes as a new animation. See WMG1-1084.
  // https://medium.com/@khalidoghli7/e89c96030c04
  void content?.offsetWidth;

  // The fallback timer starts immediately, but the hide animation starts on
  // the next frame. BUFFER_DURATION lets `animationend` win that race.
  const cancel = whenAnimationEnds(
    overlay,
    onClosed,
    ANIMATION_DURATION + BUFFER_DURATION,
  );

  requestAnimationFrame(() => {
    overlay?.classList.add(OVERLAY_CLASS.hide);
    content?.classList.add(...CONTENT_CLASS.hide);

    if (content) {
      content.style.transform = getSideTransform({
        side,
        width: getPanelWidth(panelWidth),
      });
    }
  });

  return cancel;
}
